
import { FcGoogle } from 'react-icons/fc';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthForm } from "@/hooks/useAuthForm";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';

interface SignInFormProps {
  returnTo?: string;
}

export function SignInForm({ returnTo = '/' }: SignInFormProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { 
    email, 
    setEmail, 
    password, 
    setPassword, 
    loading: formLoading, 
    handleSignIn, 
    handleGoogleSignIn
  } = useAuthForm();
  
  const [error, setError] = useState<string | null>(null);
  const { refreshSession, returnUrl } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    try {
      const result = await handleSignIn(e);
      
      if (result.error) {
        setError(result.error.message || "An error occurred during sign in");
        setLoading(false);
        return;
      }
      
      setTimeout(async () => {
        await refreshSession();
        setLoading(false);
        const redirectPath = returnTo && returnTo !== '/' ? returnTo : returnUrl;
        console.log(`[SignInForm] Redirecting to: ${redirectPath}`);
        navigate(redirectPath);
      }, 500);
    } catch (err) {
      setLoading(false);
      setError("An unexpected error occurred during sign in.");
    }
  };

  const handleGoogleClick = async () => {
    setError(null);
    const redirectPath = returnTo && returnTo !== '/' ? returnTo : returnUrl;
    await handleGoogleSignIn(redirectPath);
  };

  const getSignUpUrl = () => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('mode', 'signup');
    return `?${currentParams.toString()}`;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground">Sign In</h2>
        <p className="text-sm text-muted-foreground mt-2">Welcome back! Please sign in to your account</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-4 text-sm bg-destructive/15 text-destructive rounded-lg border border-destructive/20">
            {error}
          </div>
        )}
        
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm text-muted-foreground">
              Email
            </Label>
            <Input 
              id="email" 
              type="email" 
              placeholder="your@email.com" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 px-4 text-base border-input focus:border-primary focus:ring-primary focus:ring-2 transition-all"
              required
              aria-label="Email address"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-muted-foreground">
              Password
            </Label>
            <Input 
              id="password" 
              type="password" 
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 px-4 text-base border-input focus:border-primary focus:ring-primary focus:ring-2 transition-all"
              required
              aria-label="Password"
            />
          </div>
          
          <div className="flex justify-end">
            <Link 
              to="/auth/request-reset-password"
              className="text-sm text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline"
            >
              {t('auth.forgot.password')}
            </Link>
          </div>
        </div>
        
        <div className="space-y-4">
          <Button 
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-all focus:ring-2 focus:ring-primary focus:ring-offset-2" 
            type="submit" 
            disabled={loading || formLoading}
          >
            {loading || formLoading ? t('auth.signin.loading') : t('auth.signin')}
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border"></span>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-card px-4 text-muted-foreground">or</span>
            </div>
          </div>
          
          <Button 
            variant="outline" 
            type="button" 
            onClick={handleGoogleClick}
            className="w-full h-12 font-medium border-border hover:bg-accent hover:text-accent-foreground transition-all focus:ring-2 focus:ring-primary focus:ring-offset-2"
            disabled={loading || formLoading}
          >
            <FcGoogle className="mr-3 h-5 w-5" />
            Continue with Google
          </Button>
        </div>
        
        <div className="text-center">
          <span className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link 
              to={getSignUpUrl()}
              className="text-primary hover:text-primary/90 underline-offset-4 hover:underline font-medium transition-colors"
            >
              Sign up
            </Link>
          </span>
        </div>
      </form>
    </div>
  );
}
